import { Order } from '@tickethead/sdk';

/**
 * GA4 ecommerce item.
 * See https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */
export interface Ga4Item {
  item_id?: number | string;
  item_name?: string;
  price?: number;
  quantity?: number;
}

/**
 * GA4 ecommerce object pushed to the `dataLayer`.
 * `transaction_id`, `tax` and `shipping` are only used by the `purchase` event.
 */
export interface Ga4Ecommerce {
  currency?: string;
  value?: number;
  transaction_id?: number | string;
  tax?: number;
  shipping?: number;
  items: Ga4Item[];
}

/**
 * Ticket as it is held in the cart (a prepared ticket config enriched with the
 * selected discount price in `finalPrice`).
 */
interface CartTicket {
  id: number;
  name: string;
  currency: string;
  faceValue: number;
  finalPrice?: number;
}

export function toAddToCartEvent(ticket: CartTicket): Ga4Ecommerce {
  const price = ticket.finalPrice ?? ticket.faceValue;

  return {
    currency: ticket.currency,
    value: price,
    items: [
      {
        item_id: ticket.id,
        item_name: ticket.name,
        price,
        quantity: 1
      }
    ]
  };
}

function toGa4Items(order: Order): Ga4Item[] {
  return (
    order.orderItem?.map((orderItem) => {
      const firstTicket = orderItem.ticket?.[0];
      const ticketConfig = firstTicket?.ticketConfig;

      return {
        item_id: ticketConfig?.id,
        item_name: ticketConfig?.name,
        price: (firstTicket as any)?.grossPrice, // Remove when the field is added to the type
        quantity: orderItem.ticket?.length
      };
    }) ?? []
  );
}

export function toBeginCheckoutEvent(order: Order): Ga4Ecommerce {
  return {
    currency: order.currency,
    value: order.total,
    items: toGa4Items(order)
  };
}

export function toPurchaseEvent(order: Order): Ga4Ecommerce {
  return {
    transaction_id: order.id,
    value: order.total,
    tax: order.tax ?? 0,
    shipping: order.passedOnFee ?? 0,
    currency: order.currency,
    items: toGa4Items(order)
  };
}
